import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ServicePrincipal } from '@/types';
import { Bot, Key, User, AppWindow } from 'lucide-react';
import { useServicePrincipals } from '@/store/service-principal';
import { Button } from '@/components/ui/Button';
import { useLayout } from '@/contexts/layout-context';
import { Loader } from '@/components/loader';
import { ComposedTable, AvatarCell, ActionCell } from '@/components/table';
import { PageLayout } from '@/components/page-layout';

export const ServicePrincipalsPage = () => {
    const [spns, setSpns] = useState<ServicePrincipal[]>([]);
    const [loading, setLoading] = useState(true);
    const listServicePrincipals = useServicePrincipals((state) => state.list);
    const navigate = useNavigate();
    const { setBreadcrumbs } = useLayout();

    useEffect(() => {
        setBreadcrumbs([{ name: 'Service Principals' }]);
        listServicePrincipals().then(setSpns).catch(console.error).finally(() => setLoading(false));
    }, [listServicePrincipals, setBreadcrumbs]);

    const columns = [
        {
            id: 'name',
            header: 'Service Principal',
            accessorKey: 'name',
            cell: ({ row }: any) => {
                const s = row.original as ServicePrincipal;
                const idx = spns.indexOf(s);
                return (
                    <AvatarCell
                        name={s.name}
                        subtitle={s.description || 'Service Principal'}
                        index={idx}
                        onClick={() => navigate(`/m/service-principals/${s.id}`)}
                    />
                );
            },
        },
        {
            id: 'client_id',
            header: 'Client ID',
            cell: ({ row }: any) => {
                const s = row.original as ServicePrincipal;
                return (
                    <div className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400">
                        <Key className="w-3.5 h-3.5" />
                        <span className="font-mono">{s.client_id}</span>
                    </div>
                );
            },
        },
        {
            id: 'link',
            header: 'Linked To',
            cell: ({ row }: any) => {
                const s = row.original as ServicePrincipal;
                if (s.application_id) {
                    return (
                        <div className="flex items-center gap-1.5 text-xs text-blue-600 dark:text-blue-400">
                            <AppWindow className="w-3.5 h-3.5" />
                            <span>Application</span>
                        </div>
                    );
                }
                if (s.user_id) {
                    return (
                        <div className="flex items-center gap-1.5 text-xs text-emerald-600 dark:text-emerald-400">
                            <User className="w-3.5 h-3.5" />
                            <span>User</span>
                        </div>
                    );
                }
                return <span className="text-xs text-slate-400">—</span>;
            },
        },
        {
            id: 'created_at',
            header: 'Created Date',
            cell: ({ row }: any) => {
                const s = row.original as ServicePrincipal;
                if (!s.created_at) return <span className="text-[13px] text-slate-400">—</span>;
                const date = new Date(s.created_at).toLocaleDateString('en-US', {
                    month: 'short', day: 'numeric', year: 'numeric',
                });
                return <span className="text-[13px] text-slate-500 dark:text-slate-400">{date}</span>;
            },
        },
        {
            id: 'actions',
            header: 'Actions',
            meta: { align: 'right' as const },
            cell: ({ row }: any) => <ActionCell onClick={() => navigate(`/m/service-principals/${row.original.id}`)} />,
        },
    ];

    const emptyState = (
        <div className="p-12 text-center flex flex-col items-center justify-center bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-900">
            <div className="w-16 h-16 bg-slate-50 dark:bg-slate-800 rounded-full flex items-center justify-center mb-4">
                <Bot className="w-8 h-8 text-slate-400" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white">No service principals found</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1 max-w-xs">Start by creating your first service principal.</p>
            <Button className="mt-6 bg-[#1e293b] hover:bg-slate-800 text-white rounded-full px-6" onClick={() => navigate('/m/service-principals/create')}>
                Create Service Principal
            </Button>
        </div>
    );

    return (
        <PageLayout title="Service Principals" description="Manage non-human service accounts and client credentials.">
            {loading ? (
                <div className="flex justify-center py-20 bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-900">
                    <Loader />
                </div>
            ) : (
                <ComposedTable
                    data={spns}
                    columns={columns}
                    searchPlaceholder="Search service principals by name or description..."
                    onAdd={() => navigate('/m/service-principals/create')}
                    emptyState={emptyState}
                    itemLabel="service principals"
                />
            )}
        </PageLayout>
    );
};
